import { useEffect, useRef, type ReactNode } from 'react';
import { Button, Input, type InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { FilterDropdownProps } from 'antd/es/table/interface';

type Props = FilterDropdownProps & {
  dataIndex: string;
  placeholder?: string;
  inputDisabled?: boolean;
  extraContent?: ReactNode;
  /** Override the input value (defaults to selectedKeys[0]). */
  value?: string;
  /** Called whenever the input text changes. Receives the raw input value. */
  onValueChange?: (value: string) => void;
  onSearch: (
    selectedKeys: React.Key[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: string,
    clearFilters?: FilterDropdownProps['clearFilters'],
  ) => void;
  onReset: (
    clearFilters: NonNullable<FilterDropdownProps['clearFilters']>,
    confirm: FilterDropdownProps['confirm'],
    dataIndex: string,
  ) => void;
};

const TableColumnSearchDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  dataIndex,
  placeholder,
  inputDisabled,
  extraContent,
  value,
  onValueChange,
  onSearch,
  onReset,
}: Props) => {
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const handleSearch = () =>
    onSearch(selectedKeys, confirm, dataIndex, clearFilters);
  const handleReset = () => {
    if (clearFilters) onReset(clearFilters, confirm, dataIndex);
  };

  const inputValue =
    value ?? (selectedKeys[0] != null ? String(selectedKeys[0]) : '');

  return (
    <div style={{ padding: 8 }}>
      <Input
        ref={inputRef}
        placeholder={placeholder ?? `Search ${dataIndex}`}
        value={inputValue}
        disabled={inputDisabled}
        onChange={(e) => {
          const next = e.target.value;
          setSelectedKeys(next ? [next] : []);
          onValueChange?.(next);
        }}
        onPressEnter={handleSearch}
        style={{ width: 188, marginBottom: 8, display: 'block' }}
      />
      {extraContent}
      <Button
        type="primary"
        onClick={handleSearch}
        icon={<SearchOutlined />}
        size="small"
        style={{ width: 90, marginRight: 8 }}
      >
        Search
      </Button>
      <Button onClick={handleReset} size="small" style={{ width: 90 }}>
        Reset
      </Button>
    </div>
  );
};

export default TableColumnSearchDropdown;
